// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import React from "react";

const enum fields {
    srcType = "srcType",
    url = "url",
    jsScript = "jsScript",
    id = "id",
}

interface ScriptWidgetProps extends WidgetProps {
    fields: {
        [fields.srcType]: string;
        [fields.url]: string;
        [fields.jsScript]: string;
        [fields.id]: string;
    };
}

type Props = ScriptWidgetProps;

const ScriptWidget = ({ fields }: Props) => {
    const refContainer = React.useRef(null);

    if (fields.srcType === "jsURL" && fields.url.length !== 0) {
        React.useEffect(() => {
            const script = document.createElement("script");
            script.type = "text/javascript";
            script.src = fields.url;
            script.async = false;
            script.defer = true;
            if (fields.id) {
                script.id = fields.id;
            }
            try {
                (refContainer.current as any)?.appendChild(script);
                // eslint-disable-next-line no-empty
            } catch (e) {}
            return () => {
                (refContainer.current as any)?.removeChild(script);
            };
        }, []);
    } else if (fields.srcType === "jsText" && fields.jsScript.length !== 0) {
        React.useEffect(() => {
            const script = document.createElement("script");
            script.type = "text/javascript";
            script.async = false;
            script.defer = true;
            const code = fields.jsScript;

            try {
                script.appendChild(document.createTextNode(code));
                (refContainer.current as any)?.appendChild(script);
            } catch (e) {
                script.text = code;
                (refContainer.current as any)?.appendChild(script);
            }
            return () => {
                (refContainer.current as any)?.removeChild(script);
            };
        }, []);
    }
    return (
        <>
            <div ref={refContainer} />
        </>
    );
};

export const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    displayName: "Script Widget",
    fieldDefinitions: [
        {
            name: fields.srcType,
            displayName: "Source Type",
            editorTemplate: "RadioButtonsField",
            options: [
                {
                    displayName: "URL",
                    value: "jsURL",
                },
                {
                    displayName: "Java Script Text",
                    value: "jsText",
                },
            ],
            defaultValue: "jsURL",
            fieldType: "General",
            sortOrder: 1,
        },
        {
            name: fields.url,
            displayName: "URL",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "URL",
            sortOrder: 2,
            isVisible: item => item.fields.srcType === "jsURL",
        },
        {
            name: fields.id,
            displayName: "ID",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "ID",
            sortOrder: 4,
            isVisible: item => item.fields.srcType === "jsURL",
        },
        {
            name: fields.jsScript,
            displayName: "Java Script Text",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "Java Script Text",
            sortOrder: 3,
            isVisible: item => item.fields.srcType === "jsText",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: ScriptWidget,
    definition,
};

export default widgetModule;
